import React, { useEffect, useState } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { Stack, Box, Typography, IconButton } from "@mui/material";
import { useDispatch } from "react-redux";
import cookie from "react-cookies";
import { ArrowLeft } from "@phosphor-icons/react";
import { v4 as uuidv4 } from "uuid";

import actions from "actions";
import PopUpMessageSVG from "assets/pop-up-message.svg";
import { useGlobalToast } from "components/GlobalToastProvider";
import { useGetTeams } from "hooks/api/useGetTeams";
import { useGetUserById } from "hooks/api/user/useGetUserById";
import { useUpdateMessageById } from "hooks/api/messages/useUpdateMessage";
import { useGetMessages } from "hooks/api/messages/useGetMessages";
import { useGetTeamMembers } from "hooks/api/useGetTeamMembers";
import { useGetMessagesParticipant } from "hooks/api/messages/useGetMessagesParticipant";
import { useGetChatNotificationStatus } from "hooks/api/messages/useGetChatNotificationStatus";
import { useSendSMSToCaregiver } from "hooks/api/messages/useSendSMSToCaregiver";
import { useUpdateMessagesParticipant } from "hooks/api/messages/useUpdateMessagesParticipant";
import { copyS3Object } from "utils/aws/copyS3Object";
import { uploadToS3 } from "utils/aws/uploadToS3";
import MessageInput from "./MessageInput";
import ThreadHeader from "./ThreadHeader";
import ThreadList from "./ThreadList";
import SendAttachmentDialog from "./SendAttachmentDialog";
import UploadDocumentDialog from "./UploadDocumentDialog";

/**
 * MessagesChat is the main component for the messages chat.
 * It is responsible for rendering the messages chat, the thread header, the thread list, and the message input.
 * It also handles the logic for sending messages, uploading documents, and updating the participant.
 * @param {Object} props - The props for the MessagesChat component.
 * @param {boolean} props.isSmallScreen - Whether the screen is small.
 * @param {string} props.memberId - The ID of the member to chat with.
 * @param {function} props.refetchPreviews - A function to refetch the chat previews.
 * @param {function} props.externalTriggerRefetch - A function to trigger a refetch of the notification status.
 * @param {string} props.redirectMsgId - The ID of the message to redirect to.
 * @param {function} props.closeModal - A function to close the modal.
 * @returns {JSX.Element} - The MessagesChat component.
 */
const MessagesChat = (props) => {
  const {
    isSmallScreen,
    memberId,
    refetchPreviews,
    externalTriggerRefetch,
    redirectMsgId,
    closeModal = null,
  } = props;
  const { showToast } = useGlobalToast();
  const currentUserId = cookie.load("userId");
  const dispatch = useDispatch();
  const [messageText, setMessageText] = useState("");
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showSendAttachmentDialog, setShowSendAttachmentDialog] = useState(false);
  const [selectedFileMessage, setSelectedFileMessage] = useState(null);
  const { teams, teamsLoading } = useGetTeams({ userId: memberId, includeClientDetails: true });
  const { messages, messagesLoading, refetchMessages } = useGetMessages(memberId, 10000);
  const { user: clientTeamMember, userLoading: clientTeamMemberLoading } = useGetUserById(memberId);
  const { participantData, participantLoading, refetchParticipant } = useGetMessagesParticipant(
    currentUserId,
    memberId
  );
  const { notificationsEnabled, refetchNotificationStatus } = useGetChatNotificationStatus(
    memberId,
    currentUserId
  );
  const { sendSMSMutationAsync } = useSendSMSToCaregiver();
  const { updateMessageAsync } = useUpdateMessageById();
  const { updateParticipant } = useUpdateMessagesParticipant();
  const hasMultipleTeams = teams?.length > 1;
  const { teamMembers, teamMembersLoading } = useGetTeamMembers(
    { teamIds: [teams?.[0]?.id] },
    !hasMultipleTeams
  );
  const chatIsLoading =
    participantLoading ||
    clientTeamMemberLoading ||
    messagesLoading ||
    teamsLoading ||
    teamMembersLoading;

  //each time a new team member is selected it is assumed a new chat has been opened and read.
  useEffect(() => {
    setMessageText("");
    if (!currentUserId || !memberId) return;

    // Mark current chat as read on chat open
    const entryUpdateData = {
      user_id: currentUserId,
      chat_id: memberId,
      update_data: {
        last_read_date: new Date(),
      },
    };

    updateParticipant(entryUpdateData, {
      onSuccess: () => {
        refetchPreviews?.();
      },
    });

    const prevMemberId = memberId; //preserve for cleanup

    return () => {
      const exitUpdateData = {
        user_id: currentUserId,
        chat_id: prevMemberId,
        update_data: {
          last_read_date: new Date(),
        },
      };

      updateParticipant(exitUpdateData, {
        onSuccess: () => {
          refetchPreviews?.();
        },
      });
    };
  }, [memberId]);

  //gives parent component access to refetch notification status. Needed to keep the notification button states in sync.
  useEffect(() => {
    if (externalTriggerRefetch) {
      externalTriggerRefetch(refetchNotificationStatus);
    }
  }, [externalTriggerRefetch, refetchNotificationStatus]);

  const messageData = {
    message: messageText,
    outbound_user_id: memberId,
    sender_user_id: currentUserId,
    client_ids: teams?.map((team) => team.clientId) ?? [],
  };

  const handleSendFiles = async (files) => {
    const uploadedMessages = [];

    for (const wrappedFile of files) {
      const file = wrappedFile.file;
      const fileUUID = uuidv4();
      const last = file.name.lastIndexOf(".");
      const fileName = file.name.substring(0, last);
      const fileExtension = file.name.substring(last + 1);

      const params = {
        file,
        key: `${clientTeamMember.id}/${fileName}-${fileUUID}.${fileExtension}`,
        bucket: process.env.AWS_SMS_FILE_BUCKET,
      };

      const upload = await uploadToS3(params);

      uploadedMessages.push({
        messageText: null,
        metadata: {
          s3Key: upload.Key,
          uploadedToClientAccount: false,
          fileType: file.type,
          fileName: file.name,
          fileSize: file.size,
        },
      });
    }
    const fileMessagesData = {
      ...messageData,
      messages: uploadedMessages,
    };

    try {
      await sendSMSMutationAsync(fileMessagesData);
      refetchParticipant();
    } catch (error) {
      showToast({ message: "Error sending message." });
    }
  };

  const handleSendSMS = async () => {
    const messageTextData = {
      ...messageData,
      //send a single message with no metadata
      messages: [{ messageText }],
    };
    if (messageText && memberId) {
      try {
        await sendSMSMutationAsync(messageTextData);
        refetchPreviews?.();
        refetchNotificationStatus();
        refetchParticipant();
        setMessageText("");
        refetchMessages();
      } catch (error) {
        showToast({ message: "Error sending message." });
      }
    }
  };

  const uploadToClientAccount = async (uploadDocType, clientIds) => {
    if (!selectedFileMessage?.metadata) return;
    const { s3Key, fileName, fileSize } = selectedFileMessage.metadata;
    const fileUUID = uuidv4();
    const last = s3Key.lastIndexOf(".");
    const fileExtension = s3Key.slice(last);
    const destinationKey = `${currentUserId}/${fileName}-${fileUUID}.${fileExtension}`;

    const input = {
      sourceKey: s3Key,
      destinationBucket: process.env.AWS_USER_DOC_BUCKET,
      sourceBucket: process.env.AWS_SMS_FILE_BUCKET,
      destinationKey,
    };

    await copyS3Object(input);

    //upload for each client if multiple are selected
    for (const id of clientIds) {
      const document = {
        filename: fileName,
        key: destinationKey,
        display_filename: fileName,
        clientId: id,
        type: uploadDocType,
        uploaded_by: currentUserId,
        file_size: fileSize,
        clinicianUserId: currentUserId,
      };

      dispatch(actions.uploadCustomerDocument(document));
    }

    try {
      await updateMessageAsync({
        messageId: selectedFileMessage.pkey,
        data: { metadata: { uploadedToClientAccount: true } },
      });
      refetchMessages();
    } catch (e) {
      console.error("Error updating message.", e);
    }
  };

  if (chatIsLoading) {
    return (
      <Stack
        sx={{
          flex: 1,
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        display: "flex",
        flexDirection: "column",
        position: "relative",
        height: "100%",
      }}
    >
      {isSmallScreen && (
        <Box sx={{ padding: "24px 24px 0" }}>
          <IconButton onClick={closeModal}>
            <ArrowLeft weight="regular" format="outline" size={24} />
          </IconButton>
        </Box>
      )}

      {memberId ? (
        <>
          <ThreadHeader
            teamMembers={teamMembers}
            teams={teams}
            clientTeamMember={clientTeamMember}
            notificationsEnabled={notificationsEnabled}
            updateParticipantMutation={updateParticipant}
            refetchPreviews={refetchPreviews}
            currentUserId={currentUserId}
            refetchNotificationStatus={refetchNotificationStatus}
            closeModal={!isSmallScreen ? closeModal : null}
          />
          <ThreadList
            messages={messages || []}
            teamMemberName={clientTeamMember?.name}
            userId={currentUserId}
            onIconPress={(message) => {
              setSelectedFileMessage(message);
              setShowUploadDialog(true);
            }}
            redirectMsgId={redirectMsgId}
            isSmallScreen={isSmallScreen}
          />
          <MessageInput
            notificationsEnabled={notificationsEnabled}
            participantData={participantData}
            onChange={setMessageText}
            onSend={handleSendSMS}
            onUploadPress={() => {
              setShowSendAttachmentDialog(true);
            }}
            message={messageText}
          />
        </>
      ) : (
        <Stack
          sx={{
            flex: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: "0 24px",
          }}
        >
          <Box component="img" src={PopUpMessageSVG} alt="" />
          <Typography variant="body1" color="text.secondary" textAlign="center">
            Select a conversation to view your messages here.
          </Typography>
        </Stack>
      )}

      <UploadDocumentDialog
        open={showUploadDialog}
        onClose={() => {
          setShowUploadDialog(false);
          setSelectedFileMessage(null);
        }}
        onUpload={uploadToClientAccount}
        teams={teams}
        fileMessage={selectedFileMessage}
      />
      <SendAttachmentDialog
        open={showSendAttachmentDialog}
        onClose={() => setShowSendAttachmentDialog(false)}
        onSendFiles={handleSendFiles}
      />
    </Stack>
  );
};

export default MessagesChat;
